'use client';

import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Clock } from 'lucide-react';
import { getListingsByIds } from '@/lib/api';
import { getRecentSearches, type RecentSearch } from '@/lib/recentSearches';
import { getRecentlyViewed, type RecentlyViewedItem } from '@/lib/recentlyViewed';
import {
  hasBrowsingHistory,
  resumeChips,
  resumeViewedIds,
  resumeViewedRail,
} from '@/lib/resumeFeed';
import { useAuthStore } from '@/stores/authStore';
import { useLayoutSafe } from '@/hooks/useLayoutSafe';
import ListingCard from '@/components/search/ListingCard';
import HomeRail, { HomeRailItem, RAIL_PHOTO_SIZES } from './HomeRail';
import { RailSkeleton } from './RailSkeleton';

/**
 * Who is this, and how much pitch do they need — plus the two trails they left.
 *
 * The judgement call for the FYP: a first-time guest who has never heard of
 * GoReal genuinely needs the $5,000 proposition, and `/` carries the site's SEO
 * weight — so the pitch is **server-rendered in full, always**. That is the
 * correct page for a crawler and for a stranger.
 *
 * Someone who has already searched here, opened a home here, or is signed in has
 * converted on the idea. For them this component sets `body[data-returning='1']`
 * after hydration and `globals.css` collapses the long-form pitch to a thin strip,
 * lifting the feed up the page.
 *
 * Doing it with a body flag rather than two component trees is deliberate: the
 * server has no way to know who this is (localStorage is client-only, auth
 * resolves after mount), so branching in the markup would mean either a
 * hydration mismatch or shipping the pitch twice. (W14 R131)
 *
 * **What it renders (W14 R137):** both halves of "where you left off" — the
 * searches they ran, as chips, and the homes they opened, as a rail. Neither
 * needs an account, so this is the one personalised block a signed-out second-time
 * visitor gets. The viewed rail is re-read from the API rather than drawn from the
 * localStorage snapshot, so a home that has since sold disappears instead of
 * offering a dead end, and a price that has moved is shown at today's number.
 */
export default function ReturningVisitor() {
  const user = useAuthStore((s) => s.user);
  const [searches, setSearches] = useState<RecentSearch[]>([]);
  const [viewed, setViewed] = useState<RecentlyViewedItem[]>([]);
  const [now, setNow] = useState(0);

  /* eslint-disable react-hooks/set-state-in-effect -- localStorage is client-only,
     so a post-hydration read is the only correct moment; it runs once. Reading the
     clock here too keeps the first render deterministic for hydration. */
  useEffect(() => {
    setSearches(getRecentSearches());
    setViewed(getRecentlyViewed());
    setNow(Date.now());
  }, []);
  /* eslint-enable react-hooks/set-state-in-effect */

  const returning = hasBrowsingHistory(searches, viewed) || !!user;

  /**
   * Collapsing the pitch removes ~5,000 px from the page. Off-screen that costs
   * nothing; measured with the reader **inside** the pitch (scrolled to 4,000 at
   * 1280) it is worth **0.860 CLS** — the page yanks out from under them a beat
   * after they start reading. So the collapse waits until the pitch is out of
   * view, which for a reader of a feed is the next scroll. See `lib/stableInsert`.
   * (W14 R140)
   */
  const pitchTop = useCallback(
    () => document.querySelector('.home-pitch')?.getBoundingClientRect().top ?? null,
    []
  );
  const safeToCollapse = useLayoutSafe(pitchTop);

  useEffect(() => {
    if (!returning || !safeToCollapse) {
      delete document.body.dataset.returning;
      return;
    }
    document.body.dataset.returning = '1';
    return () => {
      delete document.body.dataset.returning;
    };
  }, [returning, safeToCollapse]);

  const chips = now ? resumeChips(searches, now) : [];
  const viewedIds = now ? resumeViewedIds(viewed, now) : [];

  /**
   * The homes, live. `getListingsByIds` answers in the order it is asked, drops
   * ids it can no longer find, and already falls back to per-id fetches when the
   * batch endpoint fails — so a partial outage costs cards, not the rail.
   */
  const viewedQuery = useQuery({
    queryKey: ['resume-viewed', viewedIds.join(',')],
    queryFn: () => getListingsByIds(viewedIds),
    enabled: viewedIds.length > 0,
    staleTime: 60_000,
  });
  const viewedListings = resumeViewedRail(viewedIds, viewedQuery.data ?? []);

  // A reserved box only while there is something to reserve it for: the ids are
  // known synchronously, so the placeholder carries the real card count and
  // cannot outlive the fetch that justifies it.
  const waitingForViewed = viewedIds.length > 0 && viewedQuery.isPending;

  if (chips.length === 0 && viewedIds.length === 0) return null;

  return (
    <section data-resume-feed aria-label="Pick up where you left off">
      <div className="mx-auto max-w-7xl px-4 pt-6">
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-ink-faint">
          <Clock className="h-3.5 w-3.5" />
          Pick up where you left off
        </p>
      </div>

      {chips.length > 0 && (
        <div className="mx-auto max-w-7xl px-4 pt-3">
          <h3 className="sr-only">Your recent searches</h3>
          <ul className="flex flex-wrap gap-2">
            {chips.map((s) => (
              <li key={s.href}>
                <Link
                  href={s.href}
                  className="flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3.5 py-1.5 text-sm font-medium text-emerald-700 transition-colors hover:bg-emerald-500/20"
                >
                  {s.label}
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {waitingForViewed && <RailSkeleton cards={Math.min(viewedIds.length, 4)} />}

      {viewedListings.length > 0 && (
        <HomeRail
          title="Homes you looked at"
          /* R137 claimed "sold and withdrawn listings drop off" here, on the
             assumption that the catalog stops returning them. It does not: the
             repository's `publicVisible` predicate filters `internet_list` and
             `display_restricted` and says nothing about status, so
             `/listings/batch` returns an expired or sold row in full. Screenshot
             of the deployed rail proved it — a card badged "No longer listed"
             sitting directly under a sentence promising it had dropped off. Only
             a **deleted** row disappears, and `resumeViewedRail` does drop those.
             (W16 R155) */
          subtitle="Most recent first — with what the MLS says about each one now"
          count={viewedListings.length}
        >
          {viewedListings.map((l) => (
            <HomeRailItem key={l.id}>
              <ListingCard listing={l} photoSizes={RAIL_PHOTO_SIZES} viewTransitionEnabled={false} />
            </HomeRailItem>
          ))}
        </HomeRail>
      )}

      {/* The chips row needs bottom padding of its own when no rail follows it. */}
      {!waitingForViewed && viewedListings.length === 0 && <div className="pb-6" />}
    </section>
  );
}
